import { useEffect, useState } from 'react';
import { ptzApi } from '../services/api';

/**
 * Static per-driver descriptors, so components never branch on camera.manufacturer.
 *
 * Vendor conventions — which way an axis counts, how hard to throttle commands, what an
 * audio input is called on the wire, whether presets live on the camera — are protocol
 * facts, and protocol facts belong to the driver that owns the protocol. Components ask
 * what a camera needs; they do not ask what brand it is.
 *
 * Fetched once and shared. The response describes the drivers, not any camera, so it
 * cannot go stale within a session and the add-camera form can use it before a camera
 * exists.
 */
let cache = null;
let inFlight = null;

export function useCameraDrivers() {
  const [drivers, setDrivers] = useState(cache);

  useEffect(() => {
    if (cache) return;
    inFlight = inFlight || ptzApi.drivers().then(r => { cache = r.data; inFlight = null; return cache; });
    let alive = true;
    inFlight.then(d => { if (alive) setDrivers(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return drivers;
}

/**
 * The descriptor for one camera, or null until the fetch lands. Callers must cope with null
 * rather than assuming a default: guessing a brand's conventions is exactly the coupling
 * this removes, and a control that briefly does nothing beats one that briefly does the
 * opposite of what it says.
 */
export function useDriverFor(camera) {
  const drivers = useCameraDrivers();
  if (!drivers) return null;
  return drivers[camera?.manufacturer] || drivers.axis || null;
}
